// src/services/taskService.js
import axios from 'axios';
import API_BASE_URL from '../config'; // ✅ Imported from centralized config

const BASE_URL = `${API_BASE_URL}/api`;

// 🔐 Auth headers with redirect on token failure
const getAuthHeaders = () => {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    alert("🚨 Session expired or unauthorized. Please log in again.");
    window.location.href = "/login"; // Redirect to login
    throw new Error('No access token found');
  }
  return {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };
};

// 📅 Fetch date for selected year/month/week/day
export const fetchDayDate = async (year, month, week, day) => {
  try {
    const url = `${BASE_URL}/day-date/?year=${year}&month=${month}&week=${week}&day=${day}`;
    console.log("📥 Fetching day date from:", url);
    const response = await axios.get(url, { headers: getAuthHeaders() });
    console.log("📦 Day date response:", response.data);
    return response.data;
  } catch (err) {
    console.error("❌ Error fetching day date:", err.response?.data || err.message);
    if (err.response?.status === 401) {
      localStorage.removeItem('accessToken');
      window.location.href = "/login";
    }
    throw err;
  }
};

// 📋 Fetch all tasks for selected year/month/week/day
export const fetchTasks = async (year, month, week, day) => {
  try {
    const url = `${BASE_URL}/tasks/?year=${year}&month=${month}&week=${week}&day=${day}`;
    console.log("📥 Fetching tasks from:", url);
    const response = await axios.get(url, { headers: getAuthHeaders() });
    console.log("📦 Task response:", response.data);
    return response.data;
  } catch (err) {
    console.error("❌ Error fetching tasks:", err.response?.data || err.message);
    if (err.response?.status === 401) {
      localStorage.removeItem('accessToken');
      window.location.href = "/login";
    }
    throw err;
  }
};

// ➕ Add new task with derived week number
export const addTask = async (taskText, taskDate, priority, category, options = {}) => {
  try {
    // A bare "YYYY-MM-DD" string parses as UTC midnight, which lands on the
    // previous day in negative-UTC timezones. Append a local time so the day
    // is interpreted in the user's timezone.
    const date = (typeof taskDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(taskDate))
      ? new Date(`${taskDate}T00:00:00`)
      : new Date(taskDate);
    const body = {
      task_text: taskText,
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      week: Math.ceil(date.getDate() / 7),
      day: date.toLocaleDateString('en-US', { weekday: 'long' }), // Use full name (e.g., Sunday)
      full_date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`, // Use local date
      priority: priority || false,
      category: category || null,
    };
    // Contextual quick-options from the composer (e.g. quantity). parse-task
    // accepts quantity/unit/location/amount/contact — pass through when set.
    if (options && options.quantity) body.quantity = options.quantity;

    console.log("📤 Sending new task to backend:", body);

    const response = await axios.post(
      `${BASE_URL}/parse-task/`,
      body,
      { headers: getAuthHeaders() }
    );

    console.log("✅ Task added successfully:", response.data);
    return response.data;
  } catch (err) {
    console.error("❌ Error adding task:", err.response?.data || err.message);
    if (err.response?.status === 401) {
      localStorage.removeItem('accessToken');
      window.location.href = "/login";
    }
    throw err;
  }
};

// 🗑️ Delete a task on the server. Prefers the row id (always present on
// fetched/just-added tasks); falls back to the composite key the backend also
// accepts. Without this, deletes were UI-only and reappeared after refresh.
export const deleteTask = async (task) => {
  try {
    const body = task?.id != null
      ? { id: task.id }
      : {
          year: task.year,
          month: task.month,
          week: task.week,
          day: task.day,
          task_text: task.task_text || task.text,
          category: task.category ?? null,
        };
    const response = await axios.delete(`${BASE_URL}/delete-task/`, {
      headers: getAuthHeaders(),
      data: body,
    });
    return response.data;
  } catch (err) {
    console.error('❌ Error deleting task:', err.response?.data || err.message);
    if (err.response?.status === 401) {
      localStorage.removeItem('accessToken');
      window.location.href = '/login';
    }
    throw err;
  }
};

// 💡 Prefix typeahead for the task composer — returns up to 5 of the user's
// own past task texts starting with `q` (backend returns [] for q < 2 chars).
export const fetchTaskAutocomplete = async (q) => {
  if (!q || q.trim().length < 2) return [];
  try {
    const url = `${BASE_URL}/task-autocomplete/?q=${encodeURIComponent(q.trim())}`;
    const response = await axios.get(url, { headers: getAuthHeaders() });
    return response.data.suggestions || [];
  } catch (err) {
    return [];
  }
};

// ➕ Add subtasks (list of strings) to a task → returns created [{id,text,completed}]
export const addSubtasks = async (taskId, texts) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/subtasks/`,
      { task_id: taskId, subtasks: texts },
      { headers: getAuthHeaders() },
    );
    return response.data.subtasks || [];
  } catch (err) {
    console.error('❌ Error adding subtasks:', err.response?.data || err.message);
    throw err;
  }
};

// 🔄 Fetch all routines
export const fetchRoutines = async () => {
  try {
    const url = `${BASE_URL}/routine/`;
    console.log("📥 Fetching routines from:", url);
    const response = await axios.get(url, { headers: getAuthHeaders() });
    console.log("📦 Routines response:", response.data);
    return response.data.routines;
  } catch (err) {
    console.error("❌ Error fetching routines:", err);
    return [];
  }
};

// 🔄 Create new routine
export const createRoutine = async (name, description = '', frequency = 'daily') => {
  try {
    const response = await axios.post(
      `${BASE_URL}/routine/create/`,
      { name, description, frequency, category: 'Personal' },
      { headers: getAuthHeaders() }
    );
    console.log("✅ Routine created:", response.data);
    return response.data;
  } catch (err) {
    console.error("❌ Error creating routine:", err);
    throw err;
  }
};

// ✏️ Update existing task (generic)
export const updateTask = async (task) => {
  try {
    // task object matches what backend expects
    // OR we transform it? Backend expects flat dict: id, task_text, etc.
    // Let's copy relevant fields.
    const body = {
      id: task.id,
      task_text: task.task_text, // Used for lookup if ID is missing (legacy)
      new_task_text: task.text, // if text changed
      description: task.description,
      quantity: task.quantity,
      completed: task.completed,
      important: task.important,
      // Pass other context if lookup by text needed (shouldn't be if ID present)
      year: task.year,
      month: task.month,
      week: task.week,
      day: task.day,
      category: task.category
    };

    const response = await axios.post(
      // Backend only registers `update-task-status/` (urls.py). The old
      // `task/update-status/` path 404'd, so edits never persisted.
      `${BASE_URL}/update-task-status/`,
      body,
      { headers: getAuthHeaders() }
    );
    console.log("✅ Task updated:", response.data);
    return response.data;
  } catch (err) {
    console.error("❌ Error updating task:", err);
    throw err;
  }
};

// 🏪 Fetch nearby stores
export const fetchNearbyStores = async (item, lat, lng, locationText) => {
  try {
    const payload = { item };
    if (lat && lng) {
      payload.latitude = lat;
      payload.longitude = lng;
    }
    if (locationText) {
      payload.location_text = locationText;
    }

    const response = await axios.post(
      `${BASE_URL}/nearby-locations/`,
      payload,
      { headers: getAuthHeaders() }
    );
    console.log("📍 Nearby stores:", response.data);
    return response.data.locations;
  } catch (err) {
    console.error("❌ Error fetching stores:", err);
    return [];
  }
};

// 🖼️ Upload Task Image
export const uploadTaskImage = async (taskId, file) => {
  try {
    const formData = new FormData();
    formData.append('id', taskId);
    formData.append('image', file);

    const headers = getAuthHeaders();
    headers['Content-Type'] = 'multipart/form-data';

    // Must match backend URL
    const response = await axios.post(
      `${BASE_URL}/update-task-status/`,
      formData,
      { headers }
    );
    console.log("✅ Task image stored:", response.data);
    return response.data;
  } catch (err) {
    console.error("❌ Error uploading image:", err);
    throw err;
  }
};

// 🛍️ Fetch Live Deals
export const fetchLiveDeals = async () => {
  try {
    const url = `${BASE_URL}/deals/live/`;
    console.log("📥 Fetching live deals from:", url);
    const response = await axios.get(url, { headers: getAuthHeaders() });
    console.log("📦 Live deals response:", response.data);
    return response.data;
  } catch (err) {
    console.error("❌ Error fetching live deals:", err.response?.data || err.message);
    throw err;
  }
};
